import type { ApiClient } from '../client.js'
import type {
  AuthSession,
  MeResponse,
  OtpRequestRequest,
  OtpRequestResponse,
  OtpVerifyRequest,
  UpdatePreferencesRequest,
  UpdatePreferencesResponse,
} from '../types/auth.js'
import type {
  DesktopLoginCodeRequest,
  DesktopLoginCodeResponse,
  DesktopLoginExchangeRequest,
  DesktopLoginExchangeResponse,
} from '../types/desktop-login.js'

export function refreshSession(client: ApiClient): Promise<Pick<AuthSession, 'token'>> {
  return client.request<Pick<AuthSession, 'token'>>('/api/v1/auth/refresh', {
    method: 'POST',
  })
}

export function getMe(client: ApiClient): Promise<MeResponse> {
  return client.request<MeResponse>('/api/v1/auth/me')
}

export function logout(client: ApiClient): Promise<void> {
  return client.request<void>('/api/v1/auth/logout', {
    method: 'DELETE',
  })
}

/**
 * Request a one-time login/signup code. The API always responds 202 to
 * prevent email enumeration, so a resolved promise is NOT proof the email
 * is registered — the verify step auto-creates the account when needed.
 */
export function requestOtp(
  client: ApiClient,
  data: OtpRequestRequest
): Promise<OtpRequestResponse> {
  return client.request<OtpRequestResponse>('/api/v1/auth/otp/request', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * Verify a one-time code and exchange it for a bearer `{ user, token }`
 * session, auto-creating the user on first signup.
 */
export function verifyOtp(client: ApiClient, data: OtpVerifyRequest): Promise<AuthSession> {
  return client.request<AuthSession>('/api/v1/auth/otp/verify', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export function updatePreferences(
  client: ApiClient,
  data: UpdatePreferencesRequest
): Promise<UpdatePreferencesResponse> {
  return client.request<UpdatePreferencesResponse>('/api/v1/auth/preferences', {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

/**
 * Mint a one-time code that signs the DESKTOP app in as the browser's current
 * user. Called from the web `/desktop-login` page with the browser's session;
 * `state` is the nonce the desktop generated before it opened that page.
 */
export function createDesktopLoginCode(
  client: ApiClient,
  data: DesktopLoginCodeRequest
): Promise<DesktopLoginCodeResponse> {
  return client.request<DesktopLoginCodeResponse>('/api/v1/auth/desktop/code', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * Trade a one-time code for a bearer session. Called from the DESKTOP app,
 * which has no credential yet — the code plus the `state` it minted is what
 * authorizes the call, so this must be issued by the same app instance that
 * started the flow.
 */
export function exchangeDesktopLoginCode(
  client: ApiClient,
  data: DesktopLoginExchangeRequest
): Promise<DesktopLoginExchangeResponse> {
  return client.request<DesktopLoginExchangeResponse>('/api/v1/auth/desktop/exchange', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}
